import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AiProvider,
  AiResult,
  AiSettings,
  AiStatus,
  AiTestResult,
  AiUsageSummary,
  CreateAiProviderInput,
  DataEnvelope,
  LlmProvider,
  LlmProviderInfo,
  TicketAiUsage,
  UpdateAiProviderInput,
  UpdateAiSettingsInput,
} from './types';

export const aiKey = ['ai'] as const;

export function useAiStatus(enabled = true) {
  return useQuery({
    queryKey: [...aiKey, 'status'],
    enabled,
    queryFn: ({ signal }) => api<DataEnvelope<AiStatus>>('/ai/status', { signal }).then((r) => r.data),
    staleTime: 60_000,
  });
}

export function useAiCatalog() {
  return useQuery({
    queryKey: [...aiKey, 'catalog'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<LlmProviderInfo[]>>('/ai/catalog', { signal }).then((r) => r.data),
    staleTime: Infinity,
  });
}

/**
 * Hooks below take an optional `ws`: the platform console passes a client's workspace id to manage
 * that client's AI without entering it. Omitted = the workspace currently open.
 */
const scoped = (ws?: string) => ws ?? 'current';

export function useAiProviders(ws?: string) {
  return useQuery({
    queryKey: [...aiKey, 'providers', scoped(ws)],
    queryFn: ({ signal }) =>
      api<DataEnvelope<AiProvider[]>>('/ai/providers', { signal, workspaceId: ws }).then((r) => r.data),
  });
}

export function useAiSettings() {
  return useQuery({
    queryKey: [...aiKey, 'settings'],
    queryFn: ({ signal }) => api<DataEnvelope<AiSettings>>('/ai/settings', { signal }).then((r) => r.data),
  });
}

export type UsageDays = 1 | 7 | 30 | 90;

/** Requests and tokens the workspace's AI used — by model, feature and heaviest tickets (admins). */
export function useAiUsage(days: UsageDays, ws?: string) {
  return useQuery({
    queryKey: [...aiKey, 'usage', days, scoped(ws)],
    queryFn: ({ signal }) =>
      api<DataEnvelope<AiUsageSummary>>('/ai/usage', { query: { days }, signal, workspaceId: ws }).then(
        (r) => r.data,
      ),
  });
}

/** Tokens one ticket has consumed across AI features. */
export function useTicketAiUsage(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: [...aiKey, 'ticket-usage', ticketId],
    enabled,
    queryFn: ({ signal }) =>
      api<DataEnvelope<TicketAiUsage>>(`/ai/tickets/${ticketId}/usage`, { signal }).then((r) => r.data),
  });
}

function useInvalidateAi() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: aiKey });
    // The platform console's client overview lists connections too.
    void qc.invalidateQueries({ queryKey: ['platform'] });
  };
}

export function useSaveAiProvider(ws?: string) {
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateAiProviderInput | UpdateAiProviderInput }) =>
      (id
        ? api<DataEnvelope<AiProvider>>(`/ai/providers/${id}`, {
            method: 'PATCH',
            body: input,
            workspaceId: ws,
          })
        : api<DataEnvelope<AiProvider>>('/ai/providers', { method: 'POST', body: input, workspaceId: ws })
      ).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeleteAiProvider(ws?: string) {
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/ai/providers/${id}`, { method: 'DELETE', workspaceId: ws }),
    onSuccess: invalidate,
  });
}

export function useTestAiProvider(ws?: string) {
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: (id: string) =>
      api<DataEnvelope<AiTestResult>>(`/ai/providers/${id}/test`, {
        method: 'POST',
        body: {},
        workspaceId: ws,
      }).then((r) => r.data),
    onSettled: invalidate,
  });
}

export function useFetchModels(ws?: string) {
  return useMutation({
    mutationFn: (input: {
      providerId?: string;
      provider?: LlmProvider;
      apiKey?: string;
      baseUrl?: string | null;
    }) =>
      api<DataEnvelope<string[]>>('/ai/providers/models', {
        method: 'POST',
        body: input,
        workspaceId: ws,
      }).then((r) => r.data),
  });
}

export function useUpdateAiSettings() {
  const invalidate = useInvalidateAi();
  return useMutation({
    mutationFn: (input: UpdateAiSettingsInput) =>
      api<DataEnvelope<AiSettings>>('/ai/settings', { method: 'PATCH', body: input }).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useAiAssist(ticketId: string) {
  return useMutation({
    mutationFn: (feature: 'suggest-reply' | 'summarize') =>
      api<DataEnvelope<AiResult>>(`/ai/tickets/${ticketId}/${feature}`, { method: 'POST', body: {} }).then(
        (r) => r.data,
      ),
  });
}
